import {
  Component,
  ElementRef,
  Input,
  ViewChild,
  AfterViewInit,
  OnDestroy
} from '@angular/core';
import { PDFDocumentProxy, getDocument, GlobalWorkerOptions } from 'pdfjs-dist';
import { LoaderComponent } from '../common/components/loader/loader.component';

GlobalWorkerOptions.workerSrc = '/assets/pdfjs/pdf.worker.min.mjs';

@Component({
  selector: 'app-pdf-viewer',
  standalone: true,
  imports: [LoaderComponent],
  templateUrl: './pdf-viewer.component.html',
  styleUrl: './pdf-viewer.component.scss'
})
export class PdfViewerComponent implements AfterViewInit, OnDestroy {
  @Input() pdfUrl!: string;
  @Input() pdfName!: string;
  @Input() highlightText!: string;
  @Input() coordinates!: any[];

  @ViewChild('pdfContainer') pdfContainerRef!: ElementRef;
  @ViewChild('pdfOverlay') pdfOverlay!: ElementRef;

  isLoading: boolean = false;

  pdf: PDFDocumentProxy | null = null;
  scale: number = 1;

  ngAfterViewInit() {
    this.loadPdf();
  }

  async loadPdf() {
    try {
      this.isLoading = true;
      this.pdf = await getDocument(this.pdfUrl).promise;
  
      // Clear existing canvases if any
      this.pdfContainerRef.nativeElement.innerHTML = '';
  
      const containerWidth = this.pdfContainerRef.nativeElement.getBoundingClientRect().width;
      const outputScale = window.devicePixelRatio || 1;

      let firstBoxInfo: { canvas: HTMLCanvasElement; topRatio: number } | null = null;
  
      for (let pageNum = 1; pageNum <= this.pdf.numPages; pageNum++) {
        const page = await this.pdf.getPage(pageNum);
  
        // Unscaled viewport
        const unscaledViewport = page.getViewport({ scale: 1 });
  
        // Scale based on container width
        const baseScale = containerWidth / unscaledViewport.width;
        const scale = baseScale * this.scale;

        const viewport = page.getViewport({ scale });
  
        // Create canvas and set size for high DPI
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d')!;
  
        canvas.width = viewport.width * outputScale;
        canvas.height = viewport.height * outputScale;
        canvas.style.width = `${viewport.width}px`;
        canvas.style.height = `${viewport.height}px`;
        canvas.style.display = 'block';
        canvas.style.margin = '0 auto 16px auto'; // Center horizontally
  
        // Improve quality
        context.setTransform(outputScale, 0, 0, outputScale, 0, 0);

        // Render page
        await page.render({
          canvasContext: context,
          viewport: viewport
        }).promise;

        // Draw bounding box AFTER rendering the page

        const boxes = this.coordinates.filter(b => b.Page === pageNum - 1);
        boxes.forEach(b => {

          const left = b.Left * viewport.width;
          const top = b.Top * viewport.height;
          const width = b.Width * viewport.width;
          const height = b.Height * viewport.height;

          context.fillStyle = 'rgba(192, 155, 251, 0.6)'; 
          context.fillRect(left, top, width, height);

          context.strokeStyle = 'rgba(192, 155, 251, 0.8)';
          context.lineWidth = 2;
          context.strokeRect(left, top, width, height);      

          if (!firstBoxInfo) {
            firstBoxInfo = {
              canvas,
              topRatio: b.Top,
            };
          }
          
        });
  
        this.pdfContainerRef.nativeElement.appendChild(canvas);
      }

      // --- After all pages are rendered, scroll to first box ---
      if (firstBoxInfo) {
        setTimeout(() => {
          requestAnimationFrame(() => {
            const canvasTop = firstBoxInfo!.canvas.offsetTop;
            const visualTop = canvasTop + firstBoxInfo!.topRatio * firstBoxInfo!.canvas.clientHeight;
            this.pdfOverlay.nativeElement.scrollTo({
              top: visualTop - 80,
              behavior: 'smooth'
            });
          });
        }, 0);
      }
        
    } catch (err) {
      console.error('PDF load error:', err);
    } finally {
      this.isLoading = false;
    }
  }

  zoomIn() {
    this.scale += 0.2;
    this.reload();
  }

  zoomOut() {
    this.scale = Math.max(0.4, this.scale - 0.2);
    this.reload();
  }

  close() {
    this.pdf = null;
    this.pdfContainerRef.nativeElement.innerHTML = '';
    document.querySelector('.pdf-overlay')?.classList.remove('open');
  }

  async reload() {
    this.pdfContainerRef.nativeElement.innerHTML = '';
    if (this.pdf) {
      const containerWidth = this.pdfContainerRef.nativeElement.getBoundingClientRect().width;
      const outputScale = window.devicePixelRatio || 1;

      for (let pageNum = 1; pageNum <= this.pdf.numPages; pageNum++) {
        const page = await this.pdf.getPage(pageNum);
  
        // Unscaled viewport
        const unscaledViewport = page.getViewport({ scale: 1 });
  
        // Scale based on container width
        const baseScale = containerWidth / unscaledViewport.width;
        const scale = baseScale * this.scale;

        const viewport = page.getViewport({ scale });
  
        // Create canvas and set size for high DPI
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d')!;
  
        canvas.width = viewport.width * outputScale;
        canvas.height = viewport.height * outputScale;
        canvas.style.width = `${viewport.width}px`;
        canvas.style.height = `${viewport.height}px`;
        canvas.style.display = 'block';
        canvas.style.margin = '0 auto 16px auto'; // Center horizontally
  
        // Improve quality
        context.setTransform(outputScale, 0, 0, outputScale, 0, 0);
  
        // Render page
        await page.render({
          canvasContext: context,
          viewport: viewport
        }).promise;
  
        // Optional: Draw bounding boxes
        const boxes = this.coordinates.filter(b => b.Page === pageNum - 1);
        boxes.forEach(b => {
          context.strokeStyle = 'purple';
          context.lineWidth = 2;
          context.strokeRect(
            b.Left * canvas.width,
            b.Top * canvas.height,
            b.Width * canvas.width,
            b.Height * canvas.height
          );
        });
  
        this.pdfContainerRef.nativeElement.appendChild(canvas);
      }
    };
  }

  ngOnDestroy() {
    this.pdfContainerRef.nativeElement.innerHTML = '';
  }
}
